import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '../src/App';
import { useAuth } from './AuthContext';
import { Layout } from './Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

import { Alert, AlertDescription } from './ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import {
  ArrowLeft,
  FileText,
  User,
  Clock,
  Calendar,
  Database,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

interface Chart {
  chartId: string;
  patientId: string;
  auditorDecision: string;
  auditorReason: string;
  auditorName: string;
  reviewDate: Date;
  chartType: string;
}

interface BatchFile {
  id: string;
  name: string;
  fileName: string;
  uploadDate: Date;
  uploadedBy: string;
  totalCharts: number;
  processedCharts: number;
  status: string;
  chartType: string;
  description: string;
  charts: Chart[];
}

export function BatchFileDetailView() {
  const { user, token } = useAuth();
  const { selectedBatchFileData, setCurrentView } = useContext(AppContext);

  const [batchFileData, setBatchFileData] = useState<BatchFile | null>(null);
  const [decisionData, setDecisionData] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [savingCharts, setSavingCharts] = useState<Set<string>>(new Set());
  const [reasonInputs, setReasonInputs] = useState<{ [chartId: string]: string }>({});
  const [reasonTimeouts, setReasonTimeouts] = useState<{ [chartId: string]: NodeJS.Timeout }>({});

  // Check if user is admin
  // const isAdmin = user?.role === 'Admin';


  const isAdmin = user?.roles?.some(
    (role: any) => role.toLowerCase() === 'admin' || role.toLowerCase() === 'super_admin'
  );

  const fetchBatchFileData = async (id: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}audits/batch/${selectedBatchFileData?.id}/decisions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch batch file data');
      }

      const result = await response.json();
      setBatchFileData(result?.batch);
      setDecisionData(result?.decisionData)
    } catch (error) {
      console.error('Error fetching batch file data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchFileData(selectedBatchFileData?.id);
  }, []);


  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(reasonTimeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Clean up individual timeout when chart is saved
  useEffect(() => {
    const currentTimeouts = { ...reasonTimeouts };
    Object.keys(currentTimeouts).forEach(chartId => {
      if (savingCharts.has(chartId) && currentTimeouts[chartId]) {
        clearTimeout(currentTimeouts[chartId]);
        delete currentTimeouts[chartId];
      }
    });
    setReasonTimeouts(currentTimeouts);
  }, [savingCharts]);

  // Handle decision change
  // const handleDecisionChange = async (chartId: string, newDecision: string) => {
  //   if (!batchFileData) return;

  //   setSavingCharts(prev => new Set(prev).add(chartId));

  //   try {
  //     // Simulate API call
  //     await new Promise(resolve => setTimeout(resolve, 500));

  //     // Update the chart in the batch file
  //     const updatedBatchFile = {
  //       ...batchFileData,
  //       charts: batchFileData.charts.map(chart =>
  //         chart.chartId === chartId
  //           ? {
  //             ...chart,
  //             auditorDecision: newDecision,
  //             reviewDate: new Date()
  //           }
  //           : chart
  //       )
  //     };

  //     setBatchFileData(updatedBatchFile);
  //     toast.success('Decision saved automatically');
  //   } catch (error) {
  //     toast.error('Failed to update decision');
  //   } finally {
  //     setSavingCharts(prev => {
  //       const newSet = new Set(prev);
  //       newSet.delete(chartId);
  //       return newSet;
  //     });
  //   }
  // };

  const updateAuditorDecisionOrReason = async (
    id: string,
    data: { auditorDecision?: string; auditorReasoning?: string }
  ) => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}audits/auditor/decision/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ id, ...data })
    });

    if (!response.ok) throw new Error('Failed to update');

    return await response.json();
  };


  const handleDecisionChange = async (chartId: string, newDecision: string) => {
    if (!decisionData) return;

    const comparison = decisionData.find(item => item.chartId === chartId);
    if (!comparison) return;

    const { id } = comparison;

    setSavingCharts(prev => new Set(prev).add(chartId));

    try {
      const result = await updateAuditorDecisionOrReason(id, { auditorDecision: newDecision });

      // Replace the updated item in state
      const updatedDecisionData = decisionData.map(item =>
        item.id === id ? result.data : item
      );

      setDecisionData(updatedDecisionData);
      toast.success('Decision saved successfully');
    } catch (error) {
      toast.error('Failed to update decision');
    } finally {
      setSavingCharts(prev => {
        const newSet = new Set(prev);
        newSet.delete(chartId);
        return newSet;
      });
    }
  };

  // Handle reason change with auto-save debouncing
  // const handleReasonChange = async (chartId: string, newReason: string) => {
  //   if (!batchFileData) return;

  //   setSavingCharts(prev => new Set(prev).add(chartId));

  //   try {
  //     // Simulate API call
  //     await new Promise(resolve => setTimeout(resolve, 500));

  //     // Update the chart in the batch file
  //     const updatedBatchFile = {
  //       ...batchFileData,
  //       charts: batchFileData.charts.map(chart =>
  //         chart.chartId === chartId
  //           ? {
  //             ...chart,
  //             auditorReason: newReason,
  //             reviewDate: new Date()
  //           }
  //           : chart
  //       )
  //     };

  //     setBatchFileData(updatedBatchFile);
  //     // Clear the input tracking for this chart
  //     setReasonInputs(prev => {
  //       const newInputs = { ...prev };
  //       delete newInputs[chartId];
  //       return newInputs;
  //     });
  //     toast.success('Reason saved automatically');
  //   } catch (error) {
  //     toast.error('Failed to save reason');
  //   } finally {
  //     setSavingCharts(prev => {
  //       const newSet = new Set(prev);
  //       newSet.delete(chartId);
  //       return newSet;
  //     });
  //   }
  // };

  const handleReasonChange = async (chartId: string, newReason: string) => {
    if (!decisionData) return;

    // Find the matching comparison entry by chartId
    const comparison = decisionData.find(item => item.chartId === chartId);
    if (!comparison) return;

    const { id } = comparison; // this is the comparisonData.id

    setSavingCharts(prev => new Set(prev).add(chartId));

    try {
      // Make the PUT request using your helper
      const result = await updateAuditorDecisionOrReason(id, {
        auditorReasoning: newReason
      });

      // Update the local state with the response
      const updatedDecisionData = decisionData.map(item =>
        item.id === id ? result.data : item
      );
      setDecisionData(updatedDecisionData);

      // Clean up reason input state
      setReasonInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[chartId];
        return newInputs;
      });
      toast.success('Reason saved successfully');
    } catch (error) {
      toast.error('Failed to save reason');
    } finally {
      setSavingCharts(prev => {
        const newSet = new Set(prev);
        newSet.delete(chartId);
        return newSet;
      });
    }
  };



  // Auto-save reason with debouncing
  // const handleReasonInputChange = (chartId: string, value: string) => {
  //   // Update the input state immediately
  //   setReasonInputs(prev => ({
  //     ...prev,
  //     [chartId]: value
  //   }));

  //   // Clear existing timeout for this chart
  //   if (reasonTimeouts[chartId]) {
  //     clearTimeout(reasonTimeouts[chartId]);
  //   }

  //   // Set new timeout for auto-save
  //   const timeoutId = setTimeout(() => {
  //     const currentChart = comparisonData?.find(c => c.id === chartId);
  //     if (currentChart && value !== currentChart.auditorReason) {
  //       handleReasonChange(chartId, value);
  //     }
  //   }, 100); // Auto-save after 1 second of no typing

  //   setReasonTimeouts(prev => ({
  //     ...prev,
  //     [chartId]: timeoutId
  //   }));
  // };
  const handleReasonInputChange = (chartId: string, value: string) => {
    // Update the input state immediately
    setReasonInputs(prev => ({
      ...prev,
      [chartId]: value
    }));

    // Clear existing timeout for this chart
    if (reasonTimeouts[chartId]) {
      clearTimeout(reasonTimeouts[chartId]);
    }

    // Set new timeout for auto-save
    const timeoutId = setTimeout(() => {
      const currentChart = decisionData?.find(c => c.chartId === chartId); // ✅ FIXED LINE
      if (currentChart && value !== currentChart.auditorReasoning) {
        handleReasonChange(chartId, value);
      }
    }, 1800);

    setReasonTimeouts(prev => ({
      ...prev,
      [chartId]: timeoutId
    }));
  };



  // Handle back to batch manager
  const handleBackToBatchManager = () => {
    setCurrentView('batch-file-manager');
  };

  // if (!isAdmin) {
  //   return (
  //     <Layout>
  //       <div className="p-6">
  //         <Alert>
  //           <AlertTriangle className="w-4 h-4" />
  //           <AlertDescription>
  //             You don't have permission to access batch file details. This feature is only available to administrators.
  //           </AlertDescription>
  //         </Alert>
  //       </div>
  //     </Layout>
  //   );
  // }

  if (!batchFileData) {
    return (
      <Layout>
        <div className="p-6">
          <Alert>
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              No batch file data found. Please go back to the batch file manager and try again.
            </AlertDescription>
          </Alert>
          <Button onClick={handleBackToBatchManager} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Batch Manager
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <TooltipProvider>
        <div className="p-6 space-y-6">
          {/* Breadcrumb Navigation */}
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={handleBackToBatchManager}
                  className="cursor-pointer hover:text-blue-600"
                >
                  Batch Files
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{batchFileData?.batchName}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToBatchManager}
                  className="hover:bg-gray-100"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">{batchFileData?.batchName}</h1>
                  <p className="text-gray-600 mt-1 text-[16px]">
                    {batchFileData.description || 'Batch file details and chart management'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Batch Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[16px]">
                <FileText className="w-5 h-5 " />
                Batch Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">File Name</label>
                  <p className="font-medium text-sm">{batchFileData?.fileName}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">Chart Type</label>
                  <p className="font-medium">{batchFileData?.chartType}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500"># of Charts</label>
                  <p className="font-medium">{batchFileData?.totalCharts}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">Uploaded By</label>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{batchFileData?.createdByUser?.firstName}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">Upload Date</label>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">
                      {batchFileData?.createdAt ? new Date(batchFileData.createdAt).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>

              </div>
            </CardContent>
          </Card>

          {/* Charts Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[16px]">
                <Database className="w-5 h-5" />
                Charts in Batch
              </CardTitle>
              <CardDescription className='text-[14px]'>
                Edit chart audit decisions directly in the table • {decisionData?.length} charts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                {decisionData?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Chart ID</TableHead>
                        <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Client ID</TableHead>
                        <TableHead className="w-48 font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Decision</TableHead>
                        <TableHead className="w-64 font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Reason</TableHead>
                        <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Auditor</TableHead>
                        <TableHead className='font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]'>Review Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    {/* <TableBody>
                      {comparisonData?.map((chart: any) => (
                        <TableRow key={chart?.chartId} className={savingCharts.has(chart?.chartId) ? 'opacity-50' : ''}>
                          <TableCell className="font-mono text-sm">{chart?.chartId}</TableCell>
                          <TableCell className="font-mono text-sm">{chart?.patientId}</TableCell>
                          <TableCell>
                            <Select
                              value={chart?.auditorDecision || 'Select Decision'}
                              onValueChange={(value) => {
                                if (value !== 'Select Decision') {
                                  handleDecisionChange(chart.chartId, value);
                                }
                              }}
                              disabled={savingCharts.has(chart.chartId)}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select Decision" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Select Decision" disabled>Select Decision</SelectItem>
                                <SelectItem value="Approved">Approved</SelectItem>
                                <SelectItem value="Send For Correction">Send For Correction</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Textarea
                              value={reasonInputs[chart.chartId] ?? (chart.auditorReason || '')}
                              onChange={(e) => handleReasonInputChange(chart.chartId, e.target.value)}
                              placeholder="Enter reason..."
                              className="min-h-[60px] resize-none"
                              disabled={savingCharts.has(chart.chartId)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span className="text-sm">{chart.auditorName}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span className="text-sm">{chart?.reviewDate}</span>
                              {savingCharts.has(chart.chartId) && (
                                <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin" />
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody> */}
                    <TableBody>
                      {decisionData?.map((chart: any) => (
                        <TableRow
                          key={chart?.chartId}
                          className={savingCharts.has(chart?.chartId) ? 'opacity-50' : ''}
                        >
                          <TableCell className="font-medium text-sm">{chart?.chartId}</TableCell>
                          <TableCell className="font-medium text-sm">{chart?.clientId}</TableCell>

                          {/* Select Dropdown for Auditor Decision */}
                          <TableCell>
                            <Select
                              value={chart?.auditorDecision || 'Select Decision'}
                              onValueChange={(value) => {
                                if (value !== 'Select Decision') {
                                  handleDecisionChange(chart.chartId, value);
                                }
                              }}
                              disabled={savingCharts.has(chart.chartId)}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select Decision" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Select Decision" disabled>
                                  Select Decision
                                </SelectItem>
                                <SelectItem value="Approved">Approved</SelectItem>
                                <SelectItem value="Send for Correction">Send for Correction</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>

                          {/* Textarea for Auditor Reasoning */}
                          <TableCell>
                            <Textarea
                              value={reasonInputs[chart.chartId] ?? (chart.auditorReasoning || '')}
                              onChange={(e) => handleReasonInputChange(chart.chartId, e.target.value)}
                              placeholder="Enter reason..."
                              className="min-h-[60px] resize-none"
                              disabled={savingCharts.has(chart.chartId)}
                            />
                          </TableCell>

                          {/* Auditor Name */}
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span className="text-sm">
                                {chart?.auditorName ? chart.auditorName : 'N/A'}
                              </span>
                            </div>
                          </TableCell>

                          {/* Review Date + Saving Spinner */}
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span className="text-sm">
                                {chart?.reviewDate ? new Date(chart.reviewDate).toLocaleDateString() : 'N/A'}
                              </span>
                              {savingCharts.has(chart.chartId) && (
                                <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin" />
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No charts found in this batch</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>


        </div>
      </TooltipProvider>
    </Layout>
  );
}